import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useInteractionLock } from '../../hooks/useInteractionLock';
import { useMobile } from '../../hooks/useMobile';
import { useViewportSize } from '../../hooks/useViewportSize';
import styles from './Window.module.scss';

export const Window = ({
    children, close,
    fullHeight, fullWidth,
    className, size = 'xl', mobileOrigin = 'bottom',
    disableInteractionLock = false
}: WindowProps) => {

    const { width, height } = useViewportSize();
    const isMobile = useMobile();

    const windowRef = useRef<HTMLDivElement>(null);
    const [isHidden, setIsHidden] = useState(false);

    // Lock interaction if a modal is shown above the page content.
    useInteractionLock(!isHidden && !disableInteractionLock);

    useEffect(() => {
        if (windowRef.current) {
            setIsHidden(isSelfOrAncestorHidden(windowRef.current));
        }
    }, [windowRef, width, height]);

    const containerClassNames = classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
        [styles.fullWidth]: fullWidth,
        [styles.desktop]: !isMobile
    });

    const windowClassNames = classNames(styles.window, className, {
        [styles[mobileOrigin!]]: mobileOrigin,
        [styles[size!]]: size
    });

    return (
        <div className={containerClassNames} style={{ width, height }}>
            <div className={styles.backdrop} onClick={close} />
            <div className={windowClassNames} ref={windowRef}>
                {children}
            </div>
        </div>
    );
};

export type WindowProps = PropsWithChildren<{
    close: () => void;
    className?: string;
    fullHeight?: boolean;
    fullWidth?: boolean;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    mobileOrigin?: 'bottom' | 'center';
    disableInteractionLock?: boolean;
}>;

const isSelfOrAncestorHidden = (element: HTMLElement) => {

    let currentElement = element;

    while (currentElement.parentElement !== null) {

        if (window.getComputedStyle(currentElement).display === 'none') {
            return true;
        }

        currentElement = currentElement.parentElement;
    }

    return false;
};